// SBS → DMC Inventory bridge.
//
// Walk the user-defined System Breakdown Structure and emit S1000D-flavoured
// DMC strings for every "item" (General + Repairable) at every level.
//
// DMC layout (illustrative; close to the S1000D scheme):
//   <modelIdent>-<systemDiff>-<sysCode>-<subCode>-<subSubCode>-<disassyCode>-<seq>-<infoCode>-<itemLoc>
// Example: PROJECT-A-21-10-00-00-0001-040A-D
//
// SNS code allocation:
//   - The active SNS preset (estimate.dmlPlan.snsPresetId) provides system /
//     subsystem / subsubsystem codes positionally.  If SBS tree depth exceeds
//     the preset depth, the missing codes default to "00".
//
// Info code:
//   - General items → estimate.sbsPlan.generalDmType (e.g. "040" → "040A")
//   - Repairable items → estimate.sbsPlan.repairableDmType (e.g. "520" → "520A")
//   - Codes already containing a letter suffix (e.g. "ACT", "FM") pass through.
import { findSnsPreset } from "./s1000dData";

const DISASSY = "00";
const ITEM_LOC = "D";

function pad(n, w = 4) {
  return String(n).padStart(w, "0");
}

function suffixInfoCode(raw) {
  if (!raw) return "040A";
  const s = String(raw).trim();
  if (/^\d{3}$/.test(s)) return `${s}A`;
  return s;
}

function safeStr(s, fallback = "") {
  return String(s ?? "").trim() || fallback;
}

// S1000D-style applicability property value — uppercase alphanumerics, 12 max.
// Mirrors the encoder used by lib/brexSkeleton.js so manifest values join cleanly
// with the BREX <assert applicPropertyValues="..."> tokens.
export function applicPropertyValue(name) {
  return String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 12) || "UNKNOWN";
}

/**
 * Generate the full DMC inventory from estimate.sbsPlan.
 * @returns Array<{ dmc, modelIdent, systemDiff, sysCode, subCode, subSubCode,
 *                  disassyCode, seq, infoCode, itemLoc,
 *                  category: 'general'|'repairable', level: 'simple'|'intermediate'|'complex',
 *                  systemName, subsystemName, subsubsystemName, dmType }>
 */
export function buildDmcInventoryFromSbs(estimate) {
  const sbs = estimate?.sbsPlan;
  if (!sbs || !Array.isArray(sbs.systems) || sbs.systems.length === 0) return [];

  const dmlPlan = estimate?.dmlPlan || {};
  const presetId = dmlPlan.snsPresetId || "generic";
  const preset = findSnsPreset(presetId);
  const modelIdent = safeStr(dmlPlan.modelIdentCode, "PROJECT").toUpperCase();
  const systemDiff = safeStr(dmlPlan.systemDiffCode, "A").toUpperCase();

  const generalInfo = suffixInfoCode(sbs.generalDmType || "040");
  const repairableInfo = suffixInfoCode(sbs.repairableDmType || "520");

  const out = [];
  const seqCounter = new Map(); // key (sysCode/subCode/subSubCode/infoCode) → counter

  const emitRows = ({ sysCode, subCode, subSubCode, level, count, infoCode, category, dmType, systemName, subsystemName, subsubsystemName }) => {
    for (let i = 0; i < count; i++) {
      const key = `${sysCode}/${subCode}/${subSubCode}/${infoCode}`;
      const next = (seqCounter.get(key) || 0) + 1;
      seqCounter.set(key, next);
      const seq = pad(next, 4);
      const dmc = `${modelIdent}-${systemDiff}-${sysCode}-${subCode}-${subSubCode}-${DISASSY}-${seq}-${infoCode}-${ITEM_LOC}`;
      out.push({
        dmc, modelIdent, systemDiff,
        sysCode, subCode, subSubCode,
        disassyCode: DISASSY, seq, infoCode, itemLoc: ITEM_LOC,
        category, level, dmType,
        systemName, subsystemName, subsubsystemName,
        applicPropertyIdent: "sbs-system",
        applicPropertyValue: applicPropertyValue(systemName),
      });
    }
  };

  sbs.systems.forEach((sys, sIdx) => {
    const presetSys = preset?.systems?.[sIdx];
    const sysCode = presetSys?.code || "00";
    const sysName = sys.name || presetSys?.name || `System ${sIdx + 1}`;

    // System-level items → Simple complexity, subsystem/subsubsystem code "00"
    emitRows({
      sysCode, subCode: "00", subSubCode: "00",
      level: "simple", count: Number(sys.generalQty) || 0,
      infoCode: generalInfo, category: "general", dmType: sbs.generalDmType,
      systemName: sysName, subsystemName: "", subsubsystemName: "",
    });
    emitRows({
      sysCode, subCode: "00", subSubCode: "00",
      level: "simple", count: Number(sys.repairableQty) || 0,
      infoCode: repairableInfo, category: "repairable", dmType: sbs.repairableDmType,
      systemName: sysName, subsystemName: "", subsubsystemName: "",
    });

    (sys.subsystems || []).forEach((sub, subIdx) => {
      const presetSub = presetSys?.subsystems?.[subIdx];
      const subCode = presetSub?.code || "00";
      const subName = sub.name || presetSub?.name || `Sub-System ${subIdx + 1}`;

      emitRows({
        sysCode, subCode, subSubCode: "00",
        level: "intermediate", count: Number(sub.generalQty) || 0,
        infoCode: generalInfo, category: "general", dmType: sbs.generalDmType,
        systemName: sysName, subsystemName: subName, subsubsystemName: "",
      });
      emitRows({
        sysCode, subCode, subSubCode: "00",
        level: "intermediate", count: Number(sub.repairableQty) || 0,
        infoCode: repairableInfo, category: "repairable", dmType: sbs.repairableDmType,
        systemName: sysName, subsystemName: subName, subsubsystemName: "",
      });

      (sub.subsubsystems || []).forEach((ss, ssIdx) => {
        const presetSss = presetSub?.subsubsystems?.[ssIdx];
        const subSubCode = presetSss?.code || "00";
        const subSubName = ss.name || presetSss?.name || `Sub-Subsystem ${ssIdx + 1}`;

        emitRows({
          sysCode, subCode, subSubCode,
          level: "complex", count: Number(ss.generalQty) || 0,
          infoCode: generalInfo, category: "general", dmType: sbs.generalDmType,
          systemName: sysName, subsystemName: subName, subsubsystemName: subSubName,
        });
        emitRows({
          sysCode, subCode, subSubCode,
          level: "complex", count: Number(ss.repairableQty) || 0,
          infoCode: repairableInfo, category: "repairable", dmType: sbs.repairableDmType,
          systemName: sysName, subsystemName: subName, subsubsystemName: subSubName,
        });
      });
    });
  });

  return out;
}

const CSV_HEADERS = [
  "#", "DMC", "ModelIdent", "SystemDiff",
  "SysCode", "SubCode", "SubSubCode", "DisassyCode",
  "Seq", "InfoCode", "ItemLoc",
  "Category", "Complexity", "DM Type",
  "SystemName", "SubsystemName", "SubsubsystemName",
  "ApplicPropertyIdent", "ApplicPropertyValue",
];

function csvEscape(v) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildDmcInventoryCsv(rows) {
  const lines = [CSV_HEADERS.join(",")];
  rows.forEach((r, idx) => {
    lines.push([
      idx + 1, r.dmc, r.modelIdent, r.systemDiff,
      r.sysCode, r.subCode, r.subSubCode, r.disassyCode,
      r.seq, r.infoCode, r.itemLoc,
      r.category, r.level, r.dmType,
      r.systemName, r.subsystemName, r.subsubsystemName,
      r.applicPropertyIdent, r.applicPropertyValue,
    ].map(csvEscape).join(","));
  });
  return lines.join("\n");
}
